/**
 * StepControl - Renders step slider controls
 *
 * Handles both pill and regular slider variants for step controls.
 * Uses LayoutConfigContext to get step values instead of relying on additionalProps.
 */

import {Hotkey} from '@wandb/components';
import {clamp, includes} from 'lodash';
import React, {useRef} from 'react';
import {twMerge} from 'tailwind-merge';

import {recordRumDurationVital} from '../../../util/datadog/datadogRum';
import {keyDisplayName} from '../../../util/runs';
import {
  useMaybeSectionMediaSliderSync,
  useMaybeSetSectionSliderSteps,
  useMaybeSetWorkspaceSliderSteps,
} from '../../WorkspaceDrawer/Settings/hooks';
import {useLayoutConfig} from '../layout/contexts/LayoutConfigContext';
import {useMediaBrowserLoading} from '../MediaBrowserLoadingContext';
import {resolveCurrentStep} from '../utils/stepUtils';
import {CARD_DEFAULT_CLASSNAMES} from './cardStyles';
import {GenericSliderDropdown} from './GenericSliderDropdown';
import {Slider} from './Slider';
import {Title} from './Title';
import {SliderChangeSource, type StepControlProps} from './types';

export type {StepControlProps};

const DD_RUM_STEP_DWELL = 'media_gallery_step_dwell';
const DD_RUM_STEP_DWELL_DESCRIPTION =
  'Media gallery step control dwell duration';

type StepDwellInterval = {
  step: number;
  startTimeMs: number;
  startTimePerformanceMs: number;
};

export const StepControl: React.FC<StepControlProps> = ({
  control,
  index,
  config,
  stepMeta,
  columnIndex,
  rowIndex,
  stepSliderKeyboardBindings,
  activeAxes,
  layout,
}) => {
  // Default classes for step control - parent container controls sizing
  const defaultClassNames = twMerge(
    CARD_DEFAULT_CLASSNAMES,
    control.classNames
  );

  const sliderKey = stepMeta?.sliderKey ?? config.mediaSliderKey ?? '_step';
  const stepLabel = keyDisplayName({
    section: 'summary',
    name: sliderKey,
  });

  const layoutConfig = useLayoutConfig();
  const cachedSliderProps = useRef<{
    min: number;
    max: number;
    available: number[];
    value: number;
  } | null>(null);
  const dwellIntervalRef = useRef<StepDwellInterval | null>(null);

  // Get sync contexts
  const sectionSliderSync = useMaybeSectionMediaSliderSync() ?? false;
  const setSectionSliderSteps = useMaybeSetSectionSliderSteps();
  const setWorkspaceSliderSteps = useMaybeSetWorkspaceSliderSteps();

  const loading = useMediaBrowserLoading();

  // Use available steps from context (pass columnIndex for column-specific steps)
  // This ensures we get the correct steps for the column's run and slider key
  const availableSteps =
    layoutConfig?.getAvailableSteps(columnIndex) ??
    stepMeta?.sliderSteps ??
    stepMeta?.steps ??
    [];
  // Get step value from context
  const stepValue = clamp(
    layoutConfig?.getStep(columnIndex, rowIndex) ??
      resolveCurrentStep(config.stepIndex, stepMeta, sliderKey),
    availableSteps[0],
    availableSteps[availableSteps.length - 1]
  );

  const handleStepChange = (
    value: number,
    source: SliderChangeSource = SliderChangeSource.Slider
  ) => {
    if (config.mode === 'gallery' && columnIndex == null) {
      const nowPerformanceMs = performance.now();
      const nowMs = Date.now();
      const previousInterval =
        dwellIntervalRef.current?.step === stepValue
          ? dwellIntervalRef.current
          : null;
      const previousStep = previousInterval?.step ?? stepValue;
      if (value === previousStep) {
        return;
      }
      const rawDurationMs =
        previousInterval != null
          ? nowPerformanceMs - previousInterval.startTimePerformanceMs
          : undefined;

      if (previousInterval != null && rawDurationMs != null) {
        recordRumDurationVital(DD_RUM_STEP_DWELL, {
          startTime: previousInterval.startTimeMs,
          duration: rawDurationMs,
          description: DD_RUM_STEP_DWELL_DESCRIPTION,
          context: {source},
        });
      }

      dwellIntervalRef.current = {
        step: value,
        startTimeMs: nowMs,
        startTimePerformanceMs: nowPerformanceMs,
      };
    }

    if (layoutConfig) {
      layoutConfig.setStep(value, columnIndex);
    }

    // Only sync when this is a shared/global step control (columnIndex is null)
    // Individual column controls should not trigger sync
    if (columnIndex == null) {
      setSectionSliderSteps?.(value, sliderKey);
      setWorkspaceSliderSteps?.(value, sliderKey, sectionSliderSync, false);
    }
  };

  // Render label if control.type is 'label'
  // Labels should render even when step is on an axis (for row/tile headers)
  if (control.type === 'label') {
    if (stepValue === undefined) {
      return null;
    }

    return (
      <Title
        key={`step-label-${index}`}
        label={stepLabel}
        value={stepValue}
        className={defaultClassNames}
        labelPosition={control.labelPosition ?? 'inline'}
        showColon={config.mode === 'compare'}
        justifyContent={config.mode === 'compare' ? 'center' : 'start'}
      />
    );
  }

  // Step is on an axis, so don't show slider
  if (includes(activeAxes, 'step')) {
    return null;
  }

  // Keep the cache up-to-date whenever real data is present so we can display
  // it during loading transitions instead of replacing the slider with a pulse.
  if (availableSteps.length >= 1) {
    // oxlint-disable-next-line react/refs
    cachedSliderProps.current = {
      min: availableSteps[0],
      max: availableSteps[availableSteps.length - 1],
      available: availableSteps,
      value: stepValue,
    };
  }

  const isFrozen = availableSteps.length < 1 && loading;

  if (availableSteps.length < 1 && !loading) {
    return null;
  }

  // oxlint-disable-next-line react/refs
  if (isFrozen && !cachedSliderProps.current) {
    // No cached data yet (first load): fall back to pulse skeleton
    return (
      <div
        className={twMerge(
          defaultClassNames,
          'h-[50px] rounded-md bg-[light-dark(var(--moon-150),var(--moon-800))] animate-pulse'
        )}
      />
    );
  }

  // Use last-known slider state when frozen, live data otherwise
  const {min, max, available, value} = isFrozen
    ? cachedSliderProps.current!
    : {
        min: availableSteps[0],
        max: availableSteps[availableSteps.length - 1],
        available: availableSteps,
        value: stepValue,
      };

  const sliderClassName = isFrozen
    ? twMerge(defaultClassNames, 'opacity-50 pointer-events-none')
    : defaultClassNames;

  // Only include keyboard bindings when control is linked (no columnIndex)
  // Individual controls shouldn't have keyboard bindings to avoid conflicts
  const keyboardBindings =
    columnIndex == null && !isFrozen ? stepSliderKeyboardBindings : undefined;

  const showShortcutTooltip =
    keyboardBindings != null && Object.keys(keyboardBindings).length > 0;
  const infoTooltip = showShortcutTooltip ? (
    <>
      <Hotkey keys={['Primary', 'ArrowRight']} name="Next step" m={2} />
      or
      <Hotkey keys={['Primary', 'ArrowLeft']} name="Previous step" m={2} />
    </>
  ) : undefined;

  // Render card variant with popover
  if (control.variant === 'card') {
    return (
      <GenericSliderDropdown
        label={stepLabel}
        value={value}
        min={min}
        max={max}
        availableValues={available}
        snapToAvailableValues={config.snapToExistingStep ?? true}
        strideLength={config.stepStrideLength ?? 1}
        onChange={isFrozen ? () => {} : handleStepChange}
        keyboardBindings={keyboardBindings}
        className={sliderClassName}
        data-test={isFrozen ? undefined : 'step-slider-card'}
        layout={layout}
      />
    );
  }

  // Use generic Slider component for standard variant
  return (
    <Slider
      className={sliderClassName}
      min={min}
      max={max}
      current={value}
      values={available}
      label={stepLabel}
      snapToExistingValue={config.snapToExistingStep ?? true}
      strideLength={config.stepStrideLength ?? 1}
      keyboardBindings={keyboardBindings}
      onChange={isFrozen ? () => {} : handleStepChange}
      tooltipContent={
        !isFrozen && sliderKey === '_step'
          ? 'This increments every time you call wandb.log in your script'
          : undefined
      }
      infoTooltip={infoTooltip}
      data-test={isFrozen ? undefined : 'step-slider'}
      layout={layout}
    />
  );
};
